import { FileWithType, ValidationResult } from './BulkDocumentUploader.types';
import {
  ACCEPTED_EXTENSIONS,
  ACCEPTED_FORMATS,
  MAX_FILE_SIZE,
  MB,
  BULK_UPLOAD_ERROR_MESSAGES,
} from '../../utils/Constants';
import { isAcceptedFileFormat } from '../../utils/fileValidation';

/**
 * Valida el formato de un archivo
 */
export const validateFileFormat = (
  file: File,
  acceptedFormats: string[] = ACCEPTED_FORMATS,
  acceptedExtensions: string[] = ACCEPTED_EXTENSIONS
): ValidationResult => {
  if (!isAcceptedFileFormat(file, acceptedFormats, acceptedExtensions)) {
    return {
      isValid: false,
      error: `El formato de archivo solo puede ser: ${acceptedExtensions.join(', ')}`,
    };
  }
  return { isValid: true };
};

/**
 * Valida el tamaño de un archivo
 */
export const validateFileSize = (
  file: File,
  maxSize: number = MAX_FILE_SIZE
): ValidationResult => {
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: BULK_UPLOAD_ERROR_MESSAGES.FILE_SIZE_EXCEEDED,
    };
  }
  return { isValid: true };
};

/**
 * Valida si hay archivos duplicados por nombre
 */
export const validateDuplicateFiles = (
  files: FileWithType[],
  newFileName: string
): ValidationResult => {
  const duplicate = files.some(
    (f) => f.fileName.toLowerCase() === newFileName.toLowerCase()
  );
  if (duplicate) {
    return {
      isValid: false,
      error: BULK_UPLOAD_ERROR_MESSAGES.DUPLICATE_FILE,
    };
  }
  return { isValid: true };
};

/**
 * Valida que todos los archivos tengan tipo asignado
 */
export const validateAllFilesHaveType = (
  files: FileWithType[]
): ValidationResult => {
  const filesWithoutType = files.filter((f) => !f.type);
  if (filesWithoutType.length > 0) {
    return {
      isValid: false,
      error: BULK_UPLOAD_ERROR_MESSAGES.SELECT_LABEL,
    };
  }
  return { isValid: true };
};

/**
 * Genera un ID único para un archivo
 */
export const generateFileId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Formatea el tamaño del archivo en MB
 */
export const formatFileSize = (bytes: number): string => {
  return (bytes / MB).toFixed(2);
};

/**
 * Valida un archivo completo (formato y tamaño)
 */
export const validateFile = (
  file: File,
  maxFileSize: number = MAX_FILE_SIZE,
  acceptedFormats: string[] = ACCEPTED_FORMATS,
  acceptedExtensions: string[] = ACCEPTED_EXTENSIONS
): ValidationResult => {
  const formatValidation = validateFileFormat(
    file,
    acceptedFormats,
    acceptedExtensions
  );
  if (!formatValidation.isValid) {
    return formatValidation;
  }

  const sizeValidation = validateFileSize(file, maxFileSize);
  if (!sizeValidation.isValid) {
    return sizeValidation;
  }

  return { isValid: true };
};

